import { z } from "zod";
import { bookGenerationPromptBindingV1Schema, bookGenerationProviderCallV1Schema, bookGenerationProviderSchema, bookGenerationRetryLineageV1Schema, bookGenerationSourceBindingV1Schema, } from "./book-generation.js";
import { layoutPreflightResultV1Schema, normalizedLayoutBoxV1Schema } from "./book-layout.js";
import { comicStructuralLayerV1Schema } from "./comic-components.js";
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const comicVisualAnchorV1Schema = z
    .object({
    id: z.string().min(1).max(100),
    kind: z.enum(["CHARACTER", "PROP", "SETTING", "PALETTE", "STYLE"]),
    description: z.string().trim().min(1).max(2_000),
    version: z.string().min(1).max(100),
    checksumSha256: sha256Schema,
})
    .strict();
export const comicProductionBalloonV1Schema = z
    .object({
    order: z.number().int().positive().max(20),
    speaker: z.string().trim().min(1).max(80),
    text: z.string().trim().min(1).max(180),
    kind: z.enum(["DIALOGUE", "THOUGHT", "SFX"]),
})
    .strict();
export const comicContinuityStateV1Schema = z
    .object({
    protagonistWardrobe: z.string().trim().min(1).max(300),
    bicycleColor: z.string().trim().min(1).max(80),
    location: z.string().trim().min(1).max(200),
    timePhase: z.string().trim().min(1).max(100),
    propState: z.string().trim().min(1).max(500),
})
    .strict();
export const comicGeneratedPanelAssetV1Schema = z
    .object({
    id: z.string().min(1).max(160),
    objectVersionId: z.string().min(1).max(160),
    checksumSha256: sha256Schema,
    artSpecHashSha256: sha256Schema,
    provider: z.literal("LIFESCAPSULE"),
    modelId: z.literal("deterministic-vector-poc-v1"),
    referenceAnchorIds: z.array(z.string().min(1).max(100)).min(1).max(20),
})
    .strict();
export const comicProductionPanelV1Schema = z
    .object({
    panelNumber: z.number().int().positive().max(8),
    visualDirection: z.string().trim().min(12).max(2_000),
    anchorIds: z.array(z.string().min(1).max(100)).min(1).max(20),
    caption: z.string().trim().max(180),
    balloons: z.array(comicProductionBalloonV1Schema).max(8),
    continuity: comicContinuityStateV1Schema,
    asset: comicGeneratedPanelAssetV1Schema,
})
    .strict();
export const comicProductionPageV1Schema = z
    .object({
    pageNumber: z.number().int().positive().max(24),
    beat: z.string().trim().min(1).max(500),
    pageTurn: z.string().trim().max(500),
    panels: z.array(comicProductionPanelV1Schema).min(1).max(8),
})
    .strict();
export const comicProductionIssueV1Schema = z
    .object({
    version: z.literal(1),
    fixtureId: z.string().min(1).max(160),
    synthetic: z.boolean(),
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().trim().min(1).max(300),
    premise: z.string().trim().min(1).max(2_000),
    sourceSnapshotHashSha256: sha256Schema,
    anchors: z.array(comicVisualAnchorV1Schema).min(1).max(100),
    pages: z.array(comicProductionPageV1Schema).min(4).max(24),
})
    .strict();
const normalizedPointSchema = z
    .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
    .strict();
export const comicGeneratedAssetV2Schema = z
    .object({
    version: z.literal(2),
    id: z.string().min(1).max(160),
    objectVersionId: z.string().min(1).max(200),
    checksumSha256: sha256Schema,
    artSpecHashSha256: sha256Schema,
    provider: bookGenerationProviderSchema,
    modelId: z.string().min(1).max(500),
    providerRequestId: z.string().min(1).max(500).optional(),
    promptHashSha256: sha256Schema,
    referenceAnchorIds: z.array(z.string().min(1).max(100)).max(100),
    referenceAssetIds: z.array(z.string().min(1).max(160)).max(100),
})
    .strict();
export const comicLetteringTextSpanV1Schema = z
    .object({
    text: z.string().min(1).max(600),
    emphasis: z.enum(["NORMAL", "EMPHASIS", "STRONG"]),
})
    .strict();
const spokenLetteringKinds = new Set([
    "DIALOGUE",
    "JOINED_DIALOGUE",
    "WHISPER",
    "RADIO",
    "THOUGHT",
    "MEMORY",
    "SHOUT",
    "DISTRESSED",
    "OFF_PANEL",
]);
export const comicLetteringObjectV1Schema = z
    .object({
    version: z.literal(1),
    id: z.string().min(1).max(120),
    panelId: z.string().min(1).max(120),
    kind: z.enum([
        "CAPTION",
        "DIALOGUE",
        "JOINED_DIALOGUE",
        "WHISPER",
        "RADIO",
        "THOUGHT",
        "MEMORY",
        "SHOUT",
        "DISTRESSED",
        "OFF_PANEL",
        "SFX",
    ]),
    captionKind: z
        .enum([
        "SCENE",
        "TIME_LOCATION",
        "INNER_MONOLOGUE",
        "SPOKEN",
        "EDITORIAL",
        "MEMORY",
        "CLOSING",
    ])
        .optional(),
    // Optional so persisted V1 lettering remains readable. New SFX plans should
    // select an editorial sound family instead of asking the renderer to infer one.
    sfxKind: z
        .enum(["IMPACT", "MOTION", "ENVIRONMENTAL", "MECHANICAL", "MAGICAL", "QUIET"])
        .optional(),
    readingOrder: z.number().int().positive().max(200),
    accessibleOrder: z.number().int().positive().max(200),
    box: normalizedLayoutBoxV1Schema,
    textSpans: z.array(comicLetteringTextSpanV1Schema).min(1).max(30),
    fontSizePoints: z.number().min(8.5).max(96),
    fillHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
    strokeHex: z.string().regex(/^#[A-Fa-f0-9]{6}$/),
    contrastRatio: z.number().min(1).max(21),
    joinGroupId: z.string().min(1).max(120).optional(),
    speakerBinding: z
        .object({
        speakerId: z.string().min(1).max(100),
        targetKind: z.enum(["MOUTH", "HEAD", "PANEL_EDGE", "SOURCE_DEVICE"]),
        target: normalizedPointSchema,
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((object, context) => {
    if (spokenLetteringKinds.has(object.kind) && !object.speakerBinding)
        context.addIssue({
            code: "custom",
            path: ["speakerBinding"],
            message: "Every spoken lettering object must bind to an explicit speaker target.",
        });
    if (object.kind === "CAPTION" && !object.captionKind)
        context.addIssue({
            code: "custom",
            path: ["captionKind"],
            message: "Caption lettering requires an explicit editorial role.",
        });
    if (object.kind === "SFX" && object.speakerBinding)
        context.addIssue({
            code: "custom",
            path: ["speakerBinding"],
            message: "Sound effects are spatial lettering and cannot carry a speaker binding.",
        });
    if (object.kind !== "SFX" && object.sfxKind)
        context.addIssue({
            code: "custom",
            path: ["sfxKind"],
            message: "Only sound-effect lettering may select an SFX family.",
        });
    if (object.kind === "JOINED_DIALOGUE" && !object.joinGroupId)
        context.addIssue({
            code: "custom",
            path: ["joinGroupId"],
            message: "Joined dialogue requires a stable group identifier.",
        });
});
export const comicLetteringLayerV1Schema = z
    .object({
    version: z.literal(1),
    objects: z.array(comicLetteringObjectV1Schema).max(200),
})
    .strict()
    .superRefine((layer, context) => {
    const ids = layer.objects.map(({ id }) => id);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["objects"],
            message: "Lettering object IDs must be unique.",
        });
    for (const field of ["readingOrder", "accessibleOrder"]) {
        const values = layer.objects.map((object) => object[field]);
        if (new Set(values).size !== values.length)
            context.addIssue({
                code: "custom",
                path: ["objects"],
                message: `${field} values must be unique within a page.`,
            });
    }
});
export const comicPanelPlanV2Schema = z
    .object({
    version: z.literal(2),
    id: z.string().min(1).max(120),
    panelNumber: z.number().int().positive().max(20),
    box: normalizedLayoutBoxV1Schema,
    visualDirection: z.string().trim().min(12).max(4_000),
    // Optional at the storage boundary so existing V2 records remain readable.
    // Production preflight requires this before a digital artifact can advance.
    accessibleDescription: z.string().trim().min(1).max(1_500).optional(),
    anchorIds: z.array(z.string().min(1).max(100)).max(100),
    copyZones: z.array(normalizedLayoutBoxV1Schema).max(20),
    protectedRegions: z
        .array(z
        .object({
        id: z.string().min(1).max(120),
        kind: z.enum(["FACE", "MOUTH", "ACTION", "PROP", "CLUE"]),
        box: normalizedLayoutBoxV1Schema,
    })
        .strict())
        .max(100),
    continuity: comicContinuityStateV1Schema,
    asset: comicGeneratedAssetV2Schema,
})
    .strict();
export const comicPagePlanV2Schema = z
    .object({
    version: z.literal(2),
    pageNumber: z.number().int().positive().max(200),
    beat: z.string().trim().min(1).max(1_000),
    pageTurn: z.string().trim().max(1_000),
    spreadRole: z.enum(["SINGLE", "LEFT", "RIGHT", "DOUBLE_PAGE"]),
    panels: z.array(comicPanelPlanV2Schema).min(1).max(20),
    lettering: comicLetteringLayerV1Schema,
    // Optional at the storage boundary so all existing V2 page plans remain readable.
    // New structural composition can be introduced independently of lettering and art.
    structure: comicStructuralLayerV1Schema.optional(),
})
    .strict()
    .superRefine((page, context) => {
    const panelIds = new Set(page.panels.map(({ id }) => id));
    for (const [index, object] of page.lettering.objects.entries())
        if (!panelIds.has(object.panelId))
            context.addIssue({
                code: "custom",
                path: ["lettering", "objects", index, "panelId"],
                message: "Lettering must reference a panel on the same page.",
            });
    for (const [index, object] of (page.structure?.objects ?? []).entries())
        if (object.panelId && !panelIds.has(object.panelId))
            context.addIssue({
                code: "custom",
                path: ["structure", "objects", index, "panelId"],
                message: "Structural components may bind only to a panel on the same page.",
            });
});
export const comicPageFamilyV1Schema = z.enum([
    "SPLASH_REVEAL",
    "STANDARD_SEQUENTIAL",
    "DENSE_CONVERSATION",
    "ACTION_PROGRESSION",
    "MEMORY_MONTAGE",
    "QUIET_EMOTIONAL",
    "OFF_PANEL_SUSPENSE",
    "DOUBLE_PAGE_EVENT",
    "TRANSITION",
    "SILENT_SEQUENCE",
]);
export const comicPageLayoutIntentV1Schema = z
    .object({
    version: z.literal(1),
    family: comicPageFamilyV1Schema,
    density: z.enum(["SPARSE", "STANDARD", "DENSE"]),
    coordinateSpace: z.literal("NORMALIZED_TOP_LEFT"),
    readingDirection: z.literal("LEFT_TO_RIGHT_TOP_TO_BOTTOM"),
    artPlacement: z.enum(["PRESERVE_PAGE_ART", "COMPOSE_PANEL_ASSETS"]),
    panelCount: z.number().int().positive().max(20),
    letteringObjectCount: z.number().int().nonnegative().max(200),
    selectionReasons: z.array(z.string().trim().min(1).max(200)).min(1).max(12),
})
    .strict();
export const comicProductionIssueV2Schema = z
    .object({
    version: z.literal(2),
    fixtureId: z.string().min(1).max(160),
    synthetic: z.boolean(),
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().trim().min(1).max(300),
    premise: z.string().trim().min(1).max(2_000),
    sourceSnapshotHashSha256: sha256Schema,
    anchors: z.array(comicVisualAnchorV1Schema).min(1).max(100),
    pages: z.array(comicPagePlanV2Schema).min(1).max(200),
})
    .strict();
export const comicProductionIssueSchema = z.discriminatedUnion("version", [
    comicProductionIssueV1Schema,
    comicProductionIssueV2Schema,
]);
export const comicProductionStageV2Schema = z.enum([
    "SOURCE_LOCK",
    "SCRIPT_CANDIDATES",
    "SCRIPT_FINAL",
    "SCRIPT_APPROVAL",
    "VISUAL_BIBLE",
    "PAGE_THUMBNAILS",
    "COMPOSITION_INTENT",
    "SEQUENTIAL_PROOF",
    "ART_DIRECTION_APPROVAL",
    "COVER_ART",
    "PAGE_ART",
    "SEQUENCE_EVALUATION",
    "TARGETED_REPAIR",
    "FINAL_ART_ANALYSIS",
    "LETTERING_LAYOUT",
    "DIGITAL_RENDER",
    "PRINT_RENDER",
    "FINAL_PROOF",
]);
export const comicPageCompositionIntentV1Schema = z
    .object({
    version: z.literal(1),
    pageNumber: z.number().int().positive().max(200),
    family: comicPageFamilyV1Schema,
    spreadRole: z.enum(["SINGLE", "LEFT", "RIGHT", "DOUBLE_PAGE"]),
    panelGeometryHashSha256: sha256Schema,
    authoritativeTextHashSha256: sha256Schema,
    letteringObjectCount: z.number().int().nonnegative().max(200),
    copyCharacterCount: z.number().int().nonnegative().max(100_000),
    minimumDialoguePoints: z.number().min(9.5).max(96),
    minimumCaptionPoints: z.number().min(9).max(96),
    copyFitPassed: z.boolean(),
    layoutPreflight: layoutPreflightResultV1Schema,
    selectionReasons: z.array(z.string().trim().min(1).max(300)).min(1).max(20),
})
    .strict()
    .superRefine((intent, context) => {
    if (intent.copyFitPassed !== intent.layoutPreflight.passed)
        context.addIssue({
            code: "custom",
            path: ["copyFitPassed"],
            message: "Composition copy-fit state must equal the authoritative layout preflight state.",
        });
});
export const comicIssueCompositionIntentV1Schema = z
    .object({
    version: z.literal(1),
    issueId: z.string().min(1).max(200),
    sourceSnapshotHashSha256: sha256Schema,
    policyId: z.string().min(1).max(160),
    policyVersion: z.string().min(1).max(100),
    policyHashSha256: sha256Schema,
    pages: z.array(comicPageCompositionIntentV1Schema).min(1).max(200),
    issueCopyFitPassed: z.boolean(),
    createdAt: z.string().datetime(),
})
    .strict()
    .superRefine((intent, context) => {
    if (intent.issueCopyFitPassed !== intent.pages.every(({ copyFitPassed }) => copyFitPassed))
        context.addIssue({
            code: "custom",
            path: ["issueCopyFitPassed"],
            message: "Issue copy-fit state must be derived from every page intent.",
        });
});
export const comicArtGeometryMapV1Schema = z
    .object({
    version: z.literal(1),
    pageNumber: z.number().int().positive().max(200),
    assetId: z.string().min(1).max(160),
    objectVersionId: z.string().min(1).max(200),
    checksumSha256: sha256Schema,
    analysisMode: z.enum(["DECLARED_GEOMETRY_TEST", "PROVIDER_VISION_AND_PIXEL_METRICS"]),
    panelBoxes: z
        .array(z
        .object({ panelId: z.string().min(1).max(120), box: normalizedLayoutBoxV1Schema })
        .strict())
        .min(1)
        .max(20),
    targets: z
        .array(z
        .object({
        id: z.string().min(1).max(160),
        panelId: z.string().min(1).max(120),
        kind: z.enum(["MOUTH", "HEAD", "PANEL_EDGE", "SOURCE_DEVICE"]),
        point: normalizedPointSchema,
        confidence: z.number().min(0).max(1),
    })
        .strict())
        .max(500),
    protectedRegions: z
        .array(z
        .object({
        id: z.string().min(1).max(160),
        panelId: z.string().min(1).max(120),
        kind: z.enum(["FACE", "MOUTH", "ACTION", "PROP", "CLUE"]),
        box: normalizedLayoutBoxV1Schema,
        confidence: z.number().min(0).max(1),
    })
        .strict())
        .max(1_000),
    copyZones: z.array(normalizedLayoutBoxV1Schema).max(200),
    bakedTextFindings: z.array(z.string().trim().min(1).max(500)).max(100),
    pixelMetricsComplete: z.boolean(),
    humanReviewRequired: z.boolean(),
    analyzedAt: z.string().datetime(),
})
    .strict()
    .superRefine((map, context) => {
    if (map.analysisMode === "DECLARED_GEOMETRY_TEST" && map.pixelMetricsComplete)
        context.addIssue({
            code: "custom",
            path: ["pixelMetricsComplete"],
            message: "Declared TEST geometry cannot claim completed pixel analysis.",
        });
});
export const comicLetteringPlacementResultV1Schema = z
    .object({
    version: z.literal(1),
    pageNumber: z.number().int().positive().max(200),
    format: z.enum(["DIGITAL", "PRINT"]),
    sourcePageHashSha256: sha256Schema,
    artGeometryHashSha256: sha256Schema,
    placedPage: comicPagePlanV2Schema,
    layoutPreflight: layoutPreflightResultV1Schema,
    placementMode: z.enum(["PRESERVED_AUTHORED", "DETERMINISTIC_CANDIDATE_SOLVER"]),
    attemptedCandidateCount: z.number().int().nonnegative().max(100_000),
    failedClosed: z.boolean(),
})
    .strict()
    .superRefine((result, context) => {
    if (result.failedClosed === result.layoutPreflight.passed)
        context.addIssue({
            code: "custom",
            path: ["failedClosed"],
            message: "Placement must fail closed exactly when preflight does not pass.",
        });
});
export const comicProductionArtifactV2Schema = z
    .object({
    version: z.literal(2),
    artifactId: z.string().min(1).max(240),
    bookId: z.string().min(1).max(160),
    stage: comicProductionStageV2Schema,
    objectKey: z.string().min(1).max(1_024),
    objectVersionId: z.string().min(1).max(200),
    checksumSha256: sha256Schema,
    sourceBinding: bookGenerationSourceBindingV1Schema,
    promptBinding: bookGenerationPromptBindingV1Schema,
    retryLineage: bookGenerationRetryLineageV1Schema,
    prerequisiteArtifactIds: z.array(z.string().min(1).max(240)).max(100),
    inputAssetRefs: z
        .array(z
        .object({
        assetId: z.string().min(1).max(160),
        objectVersionId: z.string().min(1).max(200),
        checksumSha256: sha256Schema,
    })
        .strict())
        .max(500),
    providerCalls: z.array(bookGenerationProviderCallV1Schema).max(10),
    immutable: z.literal(true),
    createdAt: z.string().datetime(),
})
    .strict();
export const comicProductionApprovalV1Schema = z
    .object({
    version: z.literal(1),
    approvalId: z.string().min(1).max(240),
    bookId: z.string().min(1).max(160),
    stage: z.enum(["SCRIPT_APPROVAL", "ART_DIRECTION_APPROVAL", "FINAL_PROOF"]),
    sourceBinding: bookGenerationSourceBindingV1Schema,
    artifactIds: z.array(z.string().min(1).max(240)).min(1).max(500),
    policyHashSha256: sha256Schema,
    approvedBy: z.string().min(1).max(200),
    approvedAt: z.string().datetime(),
})
    .strict();
//# sourceMappingURL=comic-production.js.map